"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { useAuth, type GrantSummary } from "@/contexts/AuthContext";
import { apiClient, setActivePartnerCode as syncApiClient } from "@/lib/apiClient";
import { mergePartnerOptions, type PartnerOption } from "@/lib/partnerOptions";

const STORAGE_KEY = "cp_active_partner";

interface ActivePartnerContextValue {
  activePartnerCode: string | null;
  activePartnerName: string | null;
  activeGrant: GrantSummary | null;
  partnerOptions: PartnerOption[];
  partnersLoading: boolean;
  setActivePartnerCode: (code: string) => void;
}

const ActivePartnerContext = createContext<ActivePartnerContextValue | null>(null);

function readStoredPartner(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function ActivePartnerProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const grants = useMemo(() => user?.grants ?? [], [user]);
  const isVidivetAdmin = user?.internalRole === "VIDIVET_ADMIN";

  const [userSelection, setUserSelection] = useState<string | null>(readStoredPartner);
  const [activePartners, setActivePartners] = useState<PartnerOption[] | null>(null);

  useEffect(() => {
    if (!isVidivetAdmin) return;
    let cancelled = false;
    apiClient
      .get<{ success: boolean; data: PartnerOption[] }>("/customer-portal/partner-codes/active")
      .then((res) => {
        if (!cancelled) setActivePartners(res.data);
      })
      .catch(() => {
        // Fall back to the admin's own grants if the full list can't be loaded
        if (!cancelled) setActivePartners([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isVidivetAdmin]);

  const partnersLoading = isVidivetAdmin && activePartners === null;

  const partnerOptions = useMemo(
    () => mergePartnerOptions(isVidivetAdmin ? (activePartners ?? []) : [], grants),
    [isVidivetAdmin, activePartners, grants],
  );

  const activePartnerCode = (() => {
    if (partnersLoading) return null;
    const valid = (code: string) => partnerOptions.some((o) => o.partnerCode === code);
    if (userSelection && valid(userSelection)) return userSelection;
    if (grants.length > 0) return grants[0].partnerCode;
    return isVidivetAdmin ? (partnerOptions[0]?.partnerCode ?? null) : null;
  })();

  // Sync during render, not in an effect: a switch remounts the page, whose mount fetch runs
  // before any ancestor effect, so an effect here would leave that fetch on the old partner.
  syncApiClient(activePartnerCode);

  const setActivePartnerCode = useCallback(
    (code: string) => {
      if (!partnerOptions.some((o) => o.partnerCode === code)) return;
      setUserSelection(code);
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch {}
    },
    [partnerOptions],
  );

  const activeGrant = grants.find((g) => g.partnerCode === activePartnerCode) ?? null;
  const activePartnerName =
    partnerOptions.find((o) => o.partnerCode === activePartnerCode)?.partnerName ?? null;

  return (
    <ActivePartnerContext.Provider
      value={{ activePartnerCode, activePartnerName, activeGrant, partnerOptions, partnersLoading, setActivePartnerCode }}
    >
      {children}
    </ActivePartnerContext.Provider>
  );
}

export function useActivePartner(): ActivePartnerContextValue {
  const ctx = useContext(ActivePartnerContext);
  if (!ctx) throw new Error("useActivePartner must be used within ActivePartnerProvider");
  return ctx;
}
